import { ACCOUNT_TYPES, type AccountType } from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiList } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  Notice,
  SchoolSelect,
  errorMessage,
  primaryButton,
  secondaryButton,
  selectClass,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';
import { formatMinor, parseMajor } from '../../lib/money';

type Tab = 'accounts' | 'journal' | 'reports';

interface Account {
  id: string;
  code: string;
  name: string;
  accountType: AccountType;
  isSystem: boolean;
  isActive: boolean;
}
interface Entry {
  id: string;
  entryNumber: string;
  entryDate: string;
  memo: string;
  sourceType: string;
  totalMinor: number;
  reversalOf: string | null;
}
interface TrialBalance {
  asOf: string;
  currency: string;
  accounts: { id: string; code: string; name: string; debitMinor: number; creditMinor: number }[];
  totalDebitMinor: number;
  totalCreditMinor: number;
  balanced: boolean;
}

const useCurrency = (schoolId: string) =>
  useQuery({
    queryKey: ['acct', schoolId, 'currency'],
    queryFn: async () => (await api<{ currencyCode: string }>(`/schools/${schoolId}`)).currencyCode,
  }).data ?? 'USD';

export function AccountingPage() {
  const [schoolId, setSchoolId] = useState('');
  const [tab, setTab] = useState<Tab>('accounts');
  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <h1 className="text-2xl font-bold">{t('acct.title')}</h1>
      <SchoolSelect value={schoolId} onChange={setSchoolId} />
      {schoolId && (
        <>
          <div
            role="tablist"
            aria-label={t('acct.title')}
            className="flex flex-wrap gap-1 border-b border-slate-200"
          >
            {(['accounts', 'journal', 'reports'] as const).map((name) => (
              <button
                key={name}
                role="tab"
                type="button"
                aria-selected={tab === name}
                onClick={() => setTab(name)}
                className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${tab === name ? 'border-brand-700 text-brand-700' : 'border-transparent text-slate-600'}`}
              >
                {t(`acct.tab.${name}` as MessageKey)}
              </button>
            ))}
          </div>
          <div role="tabpanel">
            {tab === 'accounts' && <AccountsTab schoolId={schoolId} />}
            {tab === 'journal' && <JournalTab schoolId={schoolId} />}
            {tab === 'reports' && <ReportsTab schoolId={schoolId} />}
          </div>
        </>
      )}
    </div>
  );
}

function AccountsTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ code: '', name: '', accountType: 'expense' });
  const [message, setMessage] = useState<string | null>(null);
  const accounts = useQuery({
    queryKey: ['acct', schoolId, 'accounts'],
    queryFn: () => api<Account[]>(`/schools/${schoolId}/accounting/accounts`),
  });
  const add = useMutation({
    mutationFn: (body: object) =>
      api(`/schools/${schoolId}/accounting/accounts`, { method: 'POST', body }),
    onSuccess: () => {
      setForm({ code: '', name: '', accountType: form.accountType });
      setMessage(null);
      void queryClient.invalidateQueries({ queryKey: ['acct', schoolId] });
    },
    onError: (e) => setMessage(errorMessage(e)),
  });
  return (
    <section aria-label={t('acct.tab.accounts')} className="space-y-4">
      {message && <Notice kind="error">{message}</Notice>}
      <table className="w-full text-left text-sm">
        <thead className="text-xs uppercase text-slate-600">
          <tr>
            <th scope="col" className="p-2">
              {t('acct.code')}
            </th>
            <th scope="col" className="p-2">
              {t('acct.name')}
            </th>
            <th scope="col" className="p-2">
              {t('acct.type')}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {accounts.data?.map((a) => (
            <tr key={a.id}>
              <td className="p-2 font-mono">{a.code}</td>
              <td className="p-2">
                {a.name}
                {a.isSystem && (
                  <span className="ml-2 text-xs text-slate-500">({t('acct.system')})</span>
                )}
              </td>
              <td className="p-2">{t(`acct.type.${a.accountType}` as MessageKey)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {can('accounting.manage', schoolId) && (
        <form
          noValidate
          aria-label={t('acct.addAccount')}
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate(form);
          }}
          className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-4"
        >
          <TextField
            label={t('acct.code')}
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
          />
          <TextField
            label={t('acct.name')}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <div>
            <label htmlFor="acct-type" className="block text-sm font-medium">
              {t('acct.type')}
            </label>
            <select
              id="acct-type"
              value={form.accountType}
              onChange={(e) => setForm({ ...form, accountType: e.target.value })}
              className={selectClass}
            >
              {ACCOUNT_TYPES.map((x) => (
                <option key={x} value={x}>
                  {t(`acct.type.${x}` as MessageKey)}
                </option>
              ))}
            </select>
          </div>
          <div className="self-end">
            <button type="submit" className={primaryButton} disabled={add.isPending}>
              {t('acct.addAccount')}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

interface LineDraft {
  accountId: string;
  debit: string;
  credit: string;
}
const blank = (): LineDraft => ({ accountId: '', debit: '', credit: '' });

function JournalTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const currency = useCurrency(schoolId);
  const [memo, setMemo] = useState('');
  const [date, setDate] = useState(new Date().toLocaleDateString('en-CA'));
  const [lines, setLines] = useState<LineDraft[]>([blank(), blank()]);
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const accounts = useQuery({
    queryKey: ['acct', schoolId, 'accounts'],
    queryFn: () => api<Account[]>(`/schools/${schoolId}/accounting/accounts`),
  });
  const entries = useQuery({
    queryKey: ['acct', schoolId, 'journal'],
    queryFn: () =>
      apiList<Entry>(`/schools/${schoolId}/accounting/journal`, { query: { pageSize: 25 } }).then(
        (r) => r.data,
      ),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['acct', schoolId] });
  const post = useMutation({
    mutationFn: (body: object) =>
      api<{ entryNumber: string }>(`/schools/${schoolId}/accounting/journal`, {
        method: 'POST',
        body,
      }),
    onSuccess: (e) => {
      setMessage({ kind: 'ok', text: t('acct.posted', { number: e.entryNumber }) });
      setMemo('');
      setLines([blank(), blank()]);
      refresh();
    },
    onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
  });
  const reverse = useMutation({
    mutationFn: (id: string) =>
      api(`/schools/${schoolId}/accounting/journal/${id}/reverse`, {
        method: 'POST',
        body: { reason: reasons[id] },
      }),
    onSuccess: refresh,
    onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
  });

  const setLine = (i: number, patch: Partial<LineDraft>) =>
    setLines(lines.map((l, k) => (k === i ? { ...l, ...patch } : l)));

  return (
    <section aria-label={t('acct.tab.journal')} className="space-y-4">
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      {can('accounting.post', schoolId) && (
        <form
          noValidate
          aria-label={t('acct.post')}
          onSubmit={(e) => {
            e.preventDefault();
            const parsed = lines.map((l) => ({
              accountId: l.accountId,
              debitMinor: l.debit ? parseMajor(l.debit, currency) : 0,
              creditMinor: l.credit ? parseMajor(l.credit, currency) : 0,
            }));
            const valid =
              memo.trim() !== '' &&
              parsed.every(
                (l) =>
                  l.accountId &&
                  l.debitMinor !== null &&
                  l.creditMinor !== null &&
                  l.debitMinor > 0 !== l.creditMinor > 0,
              );
            const debit = parsed.reduce((s, l) => s + (l.debitMinor ?? 0), 0);
            const credit = parsed.reduce((s, l) => s + (l.creditMinor ?? 0), 0);
            if (!valid || debit !== credit)
              return setMessage({ kind: 'error', text: t('acct.notBalanced') });
            setMessage(null);
            post.mutate({ entryDate: date, memo: memo.trim(), lines: parsed });
          }}
          className="space-y-3 rounded-lg border border-slate-200 bg-white p-4"
        >
          <div className="grid gap-4 sm:grid-cols-3">
            <TextField
              label={t('acct.memo')}
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
            />
            <TextField
              label={t('fees.date')}
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          {lines.map((l, i) => (
            <div key={i} className="grid items-end gap-3 sm:grid-cols-4">
              <div>
                <label
                  htmlFor={`line-account-${i}`}
                  className="block text-sm font-medium"
                >{`${t('acct.account')} ${i + 1}`}</label>
                <select
                  id={`line-account-${i}`}
                  value={l.accountId}
                  onChange={(e) => setLine(i, { accountId: e.target.value })}
                  className={selectClass}
                >
                  <option value="">—</option>
                  {accounts.data
                    ?.filter((a) => a.isActive)
                    .map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.code} {a.name}
                      </option>
                    ))}
                </select>
              </div>
              <TextField
                label={`${t('acct.debit')} ${i + 1}`}
                inputMode="decimal"
                value={l.debit}
                onChange={(e) => setLine(i, { debit: e.target.value, credit: '' })}
              />
              <TextField
                label={`${t('acct.credit')} ${i + 1}`}
                inputMode="decimal"
                value={l.credit}
                onChange={(e) => setLine(i, { credit: e.target.value, debit: '' })}
              />
              <button
                type="button"
                className={secondaryButton}
                disabled={lines.length <= 2}
                aria-label={`${t('acct.removeLine')} ${i + 1}`}
                onClick={() => setLines(lines.filter((_, k) => k !== i))}
              >
                {t('acct.removeLine')}
              </button>
            </div>
          ))}
          <div className="flex gap-2">
            <button
              type="button"
              className={secondaryButton}
              onClick={() => setLines([...lines, blank()])}
            >
              {t('acct.addLine')}
            </button>
            <button type="submit" className={primaryButton} disabled={post.isPending}>
              {t('acct.post')}
            </button>
          </div>
        </form>
      )}
      {entries.data?.length === 0 && <p className="text-slate-600">{t('acct.noEntries')}</p>}
      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
        {entries.data?.map((e) => (
          <li key={e.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
            <span>
              <span className="font-mono text-xs">{e.entryNumber}</span> · {e.entryDate} · {e.memo}{' '}
              <span className="text-slate-600">({formatMinor(e.totalMinor, currency)})</span>
            </span>
            {e.sourceType === 'manual' && !e.reversalOf && can('accounting.post', schoolId) && (
              <span className="inline-flex items-center gap-1">
                <input
                  aria-label={`${t('acct.reverseReason')}: ${e.entryNumber}`}
                  placeholder={t('acct.reverseReason')}
                  value={reasons[e.id] ?? ''}
                  onChange={(ev) => setReasons({ ...reasons, [e.id]: ev.target.value })}
                  className="w-40 rounded border border-slate-300 px-2 py-1 text-xs"
                />
                <button
                  type="button"
                  className={secondaryButton}
                  disabled={!reasons[e.id]?.trim() || reverse.isPending}
                  aria-label={`${t('acct.reverse')}: ${e.entryNumber}`}
                  onClick={() => reverse.mutate(e.id)}
                >
                  {t('acct.reverse')}
                </button>
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function ReportsTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const [from, setFrom] = useState(() =>
    new Date(Date.now() - 29 * 86_400_000).toLocaleDateString('en-CA'),
  );
  const [to, setTo] = useState(() => new Date().toLocaleDateString('en-CA'));
  const trial = useQuery({
    queryKey: ['acct', schoolId, 'trial'],
    queryFn: () => api<TrialBalance>(`/schools/${schoolId}/accounting/trial-balance`),
  });
  const collection = useQuery({
    queryKey: ['acct', schoolId, 'collection', from, to],
    queryFn: () =>
      api<{
        currency: string;
        totalMinor: number;
        receipts: number;
        byMethod: { method: string; receipts: number; totalMinor: number }[];
      }>(`/schools/${schoolId}/payments/collection-report`, { query: { from, to } }),
    enabled: can('payments.view', schoolId),
  });
  const dues = useQuery({
    queryKey: ['acct', schoolId, 'dues'],
    queryFn: async () => {
      const res = await apiList<{
        studentName: string;
        className: string | null;
        balanceMinor: number;
        overdueMinor: number;
      }>(`/schools/${schoolId}/dues-report`, { query: { pageSize: 10 } });
      return { rows: res.data, currency: (res as unknown as { currency: string }).currency };
    },
    enabled: can('fees.view', schoolId),
  });
  const tb = trial.data;
  return (
    <section aria-label={t('acct.tab.reports')} className="space-y-8">
      <div className="space-y-2">
        <h2 className="text-lg font-semibold">{t('acct.trialBalance')}</h2>
        {tb && (
          <>
            <p className={`text-sm font-medium ${tb.balanced ? 'text-green-800' : 'text-red-700'}`}>
              {tb.balanced ? t('acct.balanced') : t('acct.unbalanced')} · {t('acct.asOf')} {tb.asOf}
            </p>
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase text-slate-600">
                <tr>
                  <th scope="col" className="p-2">
                    {t('acct.account')}
                  </th>
                  <th scope="col" className="p-2 text-right">
                    {t('acct.debit')}
                  </th>
                  <th scope="col" className="p-2 text-right">
                    {t('acct.credit')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {tb.accounts.map((a) => (
                  <tr key={a.id}>
                    <td className="p-2">
                      {a.code} {a.name}
                    </td>
                    <td className="p-2 text-right">
                      {a.debitMinor ? formatMinor(a.debitMinor, tb.currency) : ''}
                    </td>
                    <td className="p-2 text-right">
                      {a.creditMinor ? formatMinor(a.creditMinor, tb.currency) : ''}
                    </td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td className="p-2">{t('acct.total')}</td>
                  <td className="p-2 text-right">{formatMinor(tb.totalDebitMinor, tb.currency)}</td>
                  <td className="p-2 text-right">
                    {formatMinor(tb.totalCreditMinor, tb.currency)}
                  </td>
                </tr>
              </tbody>
            </table>
          </>
        )}
      </div>

      <div className="space-y-2">
        <h2 className="text-lg font-semibold">{t('acct.collections')}</h2>
        <div className="flex gap-4">
          <TextField
            label={t('acct.from')}
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
          <TextField
            label={t('acct.to')}
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
        {collection.data && (
          <ul className="text-sm">
            {collection.data.byMethod.map((m) => (
              <li key={m.method}>
                {t(`fees.method.${m.method}` as MessageKey)}:{' '}
                {formatMinor(m.totalMinor, collection.data.currency)} ({m.receipts})
              </li>
            ))}
            <li className="font-semibold">
              {t('acct.total')}: {formatMinor(collection.data.totalMinor, collection.data.currency)}
            </li>
          </ul>
        )}
      </div>

      <div className="space-y-2">
        <h2 className="text-lg font-semibold">{t('acct.dues')}</h2>
        <ul className="text-sm">
          {dues.data?.rows.map((r, i) => (
            <li key={i}>
              {r.studentName} ({r.className ?? '—'}):{' '}
              {formatMinor(r.balanceMinor, dues.data.currency)}
              {r.overdueMinor > 0
                ? ` · ${t('fees.overdue')} ${formatMinor(r.overdueMinor, dues.data.currency)}`
                : ''}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
